import React, { useCallback } from "react";
import { removeChildrenRows } from "./muiPrivateApi";
import { IconButton, CircularProgress, TablePagination } from "@mui/material";
import { useGridApiContext, useGridRootProps } from "@mui/x-data-grid-premium";
import type {
  GridRenderCellParams,
  GridGroupNode,
} from "@mui/x-data-grid-premium";

interface GroupPaginationState {
  page: number;
  pageSize: number;
  totalCount: number;
}

interface GroupPaginationCellProps extends GridRenderCellParams {
  groupPaginationState: Map<string, GroupPaginationState>;
  pageSizeOptions: number[];
  onPaginationChange: (
    parentRowId: string,
    page: number,
    pageSize: number
  ) => void;
}

/**
 * Custom grouping cell that renders expand/collapse + group label + child count + pagination.
 * Built from public MUI APIs only (useGridApiContext, useGridRootProps).
 * Lives in the group header row, which is never unmounted during child pagination.
 */
export const GroupPaginationCell = React.memo(function GroupPaginationCell(
  props: GroupPaginationCellProps
) {
  const {
    groupPaginationState,
    pageSizeOptions,
    onPaginationChange,
    ...params
  } = props;
  const { id, field, rowNode: rawRowNode, formattedValue } = params;
  const rowNode = rawRowNode as GridGroupNode;

  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();

  const isExpanded = rowNode.childrenExpanded === true;
  const parentRowId = String(id);

  // Get child count via the dataSource config
  const row = apiRef.current.getRow(id);
  const childCount = row
    ? rootProps.dataSource?.getChildrenCount?.(row) ?? 0
    : 0;

  // Check if this group's children are currently loading
  const isLoading = apiRef.current.state?.dataSource?.loading?.[id] ?? false;

  // Expand/collapse handler — mirrors MUI's internal GridGroupingCriteriaCellIcon behavior
  const handleToggle = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!isExpanded) {
        apiRef.current.dataSource.fetchRows(id);
      } else {
        apiRef.current.setRowChildrenExpansion(id, false);
        removeChildrenRows(apiRef, id);
      }
      apiRef.current.setCellFocus(id, field);
    },
    [apiRef, id, field, isExpanded]
  );

  // Resolve expand/collapse icons from the grid's slot system
  const ExpandIcon = rootProps.slots.groupingCriteriaExpandIcon;
  const CollapseIcon = rootProps.slots.groupingCriteriaCollapseIcon;
  const Icon = isExpanded ? CollapseIcon : ExpandIcon;

  // Pagination state for this group
  const paginationState = groupPaginationState.get(parentRowId);
  const showPagination =
    isExpanded && paginationState && paginationState.totalCount > 0;

  // Cell content — use formattedValue if available, otherwise groupingKey
  const label =
    formattedValue !== undefined ? formattedValue : rowNode.groupingKey;

  // Indentation matching MUI's internal pattern
  const depth = rowNode.depth;
  const indentStyle =
    rootProps.rowGroupingColumnMode === "multiple"
      ? {}
      : {
          marginLeft: `calc(var(--DataGrid-cellOffsetMultiplier) * calc(var(--DataGrid-t-spacing-unit) * ${depth}))`,
        };

  return (
    <div
      className="MuiDataGrid-groupingCriteriaCell"
      style={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        ...indentStyle,
      }}
    >
      {/* Expand/collapse toggle */}
      <div
        className="MuiDataGrid-groupingCriteriaCellToggle"
        style={{ display: "flex", alignItems: "center" }}
      >
        {isLoading ? (
          <CircularProgress size="1rem" color="inherit" />
        ) : childCount > 0 || childCount === -1 ? (
          <IconButton
            size="small"
            onClick={handleToggle}
            tabIndex={-1}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <Icon fontSize="inherit" />
          </IconButton>
        ) : null}
      </div>

      {/* Group label */}
      <span>{label}</span>

      {/* Child count */}
      {childCount > 0 && (
        <span style={{ whiteSpace: "pre", paddingRight: "24px" }}>
          {" "}
          ({childCount})
        </span>
      )}

      {/* Pagination controls — stable, never unmounts while group is expanded */}
      {showPagination && (
        <TablePagination
          component="div"
          count={paginationState.totalCount}
          page={paginationState.page}
          onPageChange={(_event, newPage) => {
            _event?.preventDefault();
            onPaginationChange(parentRowId, newPage, paginationState.pageSize);
          }}
          rowsPerPage={paginationState.pageSize}
          onRowsPerPageChange={(event) => {
            const newSize = parseInt(event.target.value, 10);
            onPaginationChange(parentRowId, 0, newSize);
          }}
          rowsPerPageOptions={pageSizeOptions}
          sx={{
            flexShrink: 0,
            border: "none",
            ml: "auto",
            "& .MuiTablePagination-toolbar": {
              minHeight: 36,
              padding: 0,
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "0.75rem",
              },
          }}
        />
      )}
    </div>
  );
});
